/**
 * Wire types for the HIPAA compliance tracker.
 *
 * Copy of `obsessless-api/src/routes/hipaa/hipaaTypes.ts` -- this repo has no shared package,
 * so cross-app types are duplicated by hand (the same arrangement as `services/apiClient.ts`).
 * Keep the two in step.
 *
 * Declared as const objects plus derived unions rather than enums: db-admin compiles with
 * `erasableSyntaxOnly`, which rejects enums.
 */

export type HipaaKind = 'checklist' | 'errand' | 'vendor' | 'crosswalk';

export type HipaaPriority = 'P0' | 'P1' | 'P2';

export type HipaaSpec = 'R' | 'A';

export type HipaaStatus = 'not_started' | 'in_progress' | 'partial' | 'done' | 'na';

export type HipaaEffort = 'S' | 'M' | 'L' | 'XL';

export const HIPAA_STATUSES: HipaaStatus[] = ['not_started', 'in_progress', 'partial', 'done', 'na'];

export const STATUS_LABELS: Record<HipaaStatus, string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  partial: 'Partial',
  done: 'Done',
  na: 'N/A',
};

export const HIPAA_EFFORTS: HipaaEffort[] = ['S', 'M', 'L', 'XL'];

export const EFFORT_LABELS: Record<HipaaEffort, string> = {
  S: 'S — hours',
  M: 'M — a day or two',
  L: 'L — about a week',
  XL: 'XL — multi-week',
};

/**
 * Rough weights for "how much work is left". Deliberately super-linear: an XL item is worth
 * far more than four small ones, which is what makes the remaining-effort number useful.
 */
export const EFFORT_WEIGHTS: Record<HipaaEffort, number> = { S: 1, M: 3, L: 8, XL: 20 };

export const KIND_LABELS: Record<HipaaKind, string> = {
  checklist: 'Checklist',
  errand: 'Verification',
  vendor: 'Vendor',
  crosswalk: 'Trust list',
};

export interface HipaaVendor {
  healthData: string;
  baaStatus: string;
  disposition: string;
}

export interface HipaaActor {
  uid: string;
  email: string;
}

export interface HipaaItem {
  id: string;
  kind: HipaaKind;
  order: number;

  section: string;
  sectionTitle: string;
  parentSection: string;
  parentSectionTitle: string;
  sectionCfr: string | null;
  title: string;
  statusNote: string | null;
  actions: string | null;
  citations: string[];
  priorities: HipaaPriority[];
  priority: HipaaPriority | null;
  specs: HipaaSpec[];
  needsVerification: boolean;
  mapsTo: string[];
  vendor: HipaaVendor | null;

  status: HipaaStatus;
  statusIsExplicit: boolean;
  effort: HipaaEffort | null;
  blocked: boolean;
  blockedReason: string;
  commentCount: number;
  completedAt: string | null;
  updatedAt: string | null;
  updatedBy: HipaaActor | null;
}

export type HipaaActivityType = 'status' | 'effort' | 'blocked' | 'comment';

export interface HipaaActivityEntry {
  id: string;
  itemId: string;
  itemTitle: string;
  type: HipaaActivityType;
  from: string | null;
  to: string | null;
  text: string | null;
  actor: HipaaActor;
  at: string | null;
}

export interface HipaaComment {
  id: string;
  text: string;
  author: HipaaActor;
  createdAt: string | null;
}

export interface HipaaItemPatch {
  status?: HipaaStatus;
  statusIsExplicit?: boolean;
  effort?: HipaaEffort | null;
  blocked?: boolean;
  blockedReason?: string;
}
